import { fetchPrimary } from "../environments/primary/fetch";

// The stored plan an approval of "stop this World's subscription renewing"
// binds to (docs/world-subscription-cancel-design.md). The card's facts come
// only from here: the World's own stored snapshot, read through the local
// server under the person's own grant. Never from Cleo's words.

export interface SubscriptionPlanFacts {
  readonly planKey: string;
  readonly snapshotDigest: string;
  readonly expiresAt: number;
  readonly snapshot: {
    readonly targetWorldKey: string;
    readonly targetWorldName: string;
    readonly targetWorldStatus: string;
    readonly subscriptionStatus: string;
    readonly amountCents: number;
    readonly currency: string;
    readonly quantity: number;
    readonly interval: string;
    readonly intervalCount: number;
    readonly currentPeriodEnd: number;
  };
}

/** Not read yet, read, or refused with the reason the person is shown. */
export type SubscriptionPlanRead =
  | { readonly status: "reading" }
  | { readonly status: "loaded"; readonly plan: SubscriptionPlanFacts }
  | { readonly status: "failed"; readonly reason: string };

const READ_TIMEOUT_MS = 15_000;
const FAILED = "Neucleos could not read this plan, so it cannot be shown or approved here.";

function isPlan(value: unknown): value is SubscriptionPlanFacts {
  if (typeof value !== "object" || value === null) return false;
  const plan = value as Record<string, unknown>;
  const snapshot = plan["snapshot"] as Record<string, unknown> | null | undefined;
  return (
    typeof plan["planKey"] === "string" &&
    typeof plan["snapshotDigest"] === "string" &&
    typeof plan["expiresAt"] === "number" &&
    typeof snapshot === "object" &&
    snapshot !== null &&
    typeof snapshot["targetWorldName"] === "string" &&
    typeof snapshot["targetWorldStatus"] === "string" &&
    typeof snapshot["subscriptionStatus"] === "string" &&
    Number.isSafeInteger(snapshot["amountCents"]) &&
    typeof snapshot["currency"] === "string" &&
    Number.isSafeInteger(snapshot["quantity"]) &&
    typeof snapshot["interval"] === "string" &&
    Number.isSafeInteger(snapshot["intervalCount"]) &&
    Number.isSafeInteger(snapshot["currentPeriodEnd"])
  );
}

/** Reads the stored plan from the operator World; never throws. */
export async function readSubscriptionPlan(
  worldKey: string,
  planKey: string,
): Promise<SubscriptionPlanRead> {
  try {
    const response = await fetchPrimary("/api/neucleos/commerce/subscription-plan", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ worldKey, planKey }),
      // A read that never answers is a refusal the person sees, not a card stuck reading.
      signal: AbortSignal.timeout(READ_TIMEOUT_MS),
    });
    const body = (await response.json().catch(() => null)) as {
      ok?: boolean;
      value?: unknown;
      reason?: unknown;
    } | null;
    if (!response.ok || body?.ok !== true || !isPlan(body.value)) {
      const reason =
        typeof body?.reason === "string" && body.reason !== ""
          ? body.reason.replace(/^REFUSED:\s*/, "").split(". Law:")[0]!
          : FAILED;
      return { status: "failed", reason };
    }
    return { status: "loaded", plan: body.value };
  } catch {
    return { status: "failed", reason: FAILED };
  }
}
